// packages/frontend/src/app/providers/ToastProvider.tsx
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

type ToastType = 'info' | 'success' | 'error' | 'warning';
type Toast = { id: string; type: ToastType; message: string; createdAt: number; duration: number };

type ToastCtx = {
  toasts: ReadonlyArray<Toast>;
  addToast: (t: { type?: ToastType; message: string; duration?: number }) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
};

const ToastContext = createContext<ToastCtx | undefined>(undefined);

export default function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Record<string, any>>({});

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const tm = timers.current[id];
    if (tm) clearTimeout(tm);
    delete timers.current[id];
  }, []);

  const addToast = useCallback(({ type = 'info', message, duration = 3500 }: { type?: ToastType; message: string; duration?: number }) => {
    const id = crypto?.randomUUID?.() ?? String(Math.random());
    const toast: Toast = { id, type, message, createdAt: Date.now(), duration };
    setToasts((prev) => [toast, ...prev]);
    timers.current[id] = setTimeout(() => removeToast(id), duration);
    return id;
  }, [removeToast]);

  const clearToasts = useCallback(() => {
    Object.values(timers.current).forEach(clearTimeout);
    timers.current = {};
    setToasts([]);
  }, []);

  const value = useMemo<ToastCtx>(() => ({ toasts, addToast, removeToast, clearToasts }), [toasts, addToast, removeToast, clearToasts]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}
